import type {
  BuyAction,
  Brand,
  PropertyValue,
  UnitPriceSpecification,
  Organization,
  Offer,
  BreadcrumbList,
  OfferCatalog,
  Product,
} from "schema-dts";

import { ProductData } from "../types/schema.types";

/**
 * Generates a BuyAction schema object for product purchase actions
 * @param buyActionUrl - The URL template for the buy action
 * @returns BuyAction schema object
 */
export function generateBuyAction(buyActionUrl: string): BuyAction {
  return {
    "@type": "BuyAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: buyActionUrl,
      actionPlatform: [
        "https://schema.org/DesktopWebPlatform",
        "https://schema.org/MobileWebPlatform",
      ],
    },
  };
}

/**
 * Generates a PropertyValue schema object for additional product properties
 * @param name - The property name
 * @param value - The property value
 * @returns PropertyValue schema object
 */
export function generatePropertyValue(
  name: string,
  value: string
): PropertyValue {
  return {
    "@type": "PropertyValue",
    name,
    value,
  };
}

/**
 * Generates a Brand schema object with optional URL and sameAs links
 * @param name - The brand name
 * @param url - Optional brand URL
 * @param sameAs - Optional array of sameAs URLs (e.g. Wikidata, official site, socials)
 * @returns Brand schema object
 */
export function generateBrand(
  name: string,
  url?: string,
  sameAs?: string[]
): Brand {
  const brand: Brand = {
    "@type": "Brand",
    name,
  };

  if (url) {
    brand.url = url;
  }

  if (sameAs && sameAs.length > 0) {
    brand.sameAs = sameAs;
  }

  return brand;
}

/**
 * Generates a UnitPriceSpecification schema object
 * @param price - The price value
 * @param currency - The currency code
 * @param priceType - The price type (e.g., "SalePrice", "ListPrice")
 * @returns UnitPriceSpecification schema object
 */
export function generateUnitPriceSpecification(
  price: number,
  currency: string,
  priceType: "SalePrice" | "ListPrice" = "SalePrice"
): UnitPriceSpecification {
  return {
    "@type": "UnitPriceSpecification",
    price,
    priceCurrency: currency,
    priceType: `https://schema.org/${priceType}`,
  };
}

/**
 * Generates an array of UnitPriceSpecification objects for sale and list prices
 * @param salePrice - The sale price
 * @param currency - The currency code
 * @param listPrice - Optional list price (regular price)
 * @returns Array of UnitPriceSpecification objects
 */
export function generatePriceSpecifications(
  salePrice: number,
  currency: string,
  listPrice?: number
): UnitPriceSpecification[] {
  const specifications: UnitPriceSpecification[] = [
    generateUnitPriceSpecification(salePrice, currency, "SalePrice"),
  ];

  if (listPrice) {
    specifications.push(
      generateUnitPriceSpecification(listPrice, currency, "ListPrice")
    );
  }

  return specifications;
}

/**
 * Generates an Organization schema object for sellers
 * @param name - The organization name
 * @param url - Optional organization URL
 * @returns Organization schema object
 */
export function generateSeller(name: string, url?: string): Organization {
  const seller: Organization = {
    "@type": "Organization",
    name,
  };

  if (url) {
    seller.url = url;
  }

  return seller;
}

/**
 * Adds a PropertyValue to the additionalProperty array of a schema object
 * @param schema - The schema object to add the property to
 * @param name - The property name
 * @param value - The property value
 */
export function addAdditionalProperty(
  schema: { additionalProperty?: any },
  name: string,
  value: string
): void {
  const existingProps = Array.isArray(schema.additionalProperty)
    ? schema.additionalProperty
    : schema.additionalProperty
    ? [schema.additionalProperty]
    : [];

  schema.additionalProperty = [
    ...(existingProps as PropertyValue[]),
    generatePropertyValue(name, value),
  ];
}

/**
 * Generates an Offer schema object for product offers
 * @param price - The offer price
 * @param currency - The currency code
 * @param availability - The availability status
 * @param url - The offer URL
 * @param regularPrice - Optional regular price for price specifications
 * @param sellerName - The seller organization name
 * @param sellerUrl - Optional seller URL
 * @param sku - Optional SKU for the specific offer
 * @param size - Optional size for the specific offer
 * @param sellerIdRef - Optional @id reference for seller cross-referencing
 * @returns Offer schema object
 */
export function generateOffer(
  price: number,
  currency: string,
  availability: "InStock" | "OutOfStock" | "LimitedAvailability",
  url: string,
  regularPrice?: number,
  sellerName = "Otrium",
  sellerUrl?: string,
  sku?: string,
  size?: string,
  sellerIdRef?: string
): Offer {
  const offer: Offer = {
    "@type": "Offer",
    price,
    priceCurrency: currency,
    availability: `https://schema.org/${availability}`,
    url,
    priceSpecification: generatePriceSpecifications(
      price,
      currency,
      regularPrice
    ),
    seller: sellerIdRef
      ? ({ "@id": sellerIdRef } as any)
      : generateSeller(sellerName, sellerUrl),
  };

  // Add SKU if provided
  if (sku) {
    offer.sku = sku;
  }

  // Add size as additional property if provided
  if (size) {
    addAdditionalProperty(offer, "size", size);
  }

  return offer;
}

/**
 * Options for CollectionPage generation
 */
export interface CollectionPageOptions {
  /** Copyright holder @id reference, e.g. "https://about.otrium.com/nl#mainEntityOfPage" */
  copyrightHolderId?: string;
  /** Copyright notice text as shown in footer */
  copyrightNotice?: string;
  /** Array of years the copyright applies to */
  copyrightYear?: number[];
  /** Publisher @id reference */
  publisherId?: string;
}

/**
 * Generates a CollectionPage schema object for collection/brand pages
 * @param name - The collection page name
 * @param description - The collection page description
 * @param url - The collection page URL
 * @param inLanguage - Optional language code for the page
 * @param breadcrumbs - Optional breadcrumb items to include in the page
 * @param options - Optional copyright and publisher metadata
 * @returns CollectionPage-shaped object with @id support
 */
export function generateCollectionPage(
  name: string,
  description: string,
  url: string,
  inLanguage?: string,
  breadcrumbs?: Array<{ name: string; url?: string }>,
  options?: CollectionPageOptions
): Record<string, unknown> {
  const collectionPage: Record<string, unknown> = {
    "@type": "CollectionPage",
    "@id": url,
    name,
    description,
    url,
  };

  if (inLanguage) {
    collectionPage.inLanguage = inLanguage;
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    const breadcrumbList: BreadcrumbList = {
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((item, index) => {
        const breadcrumbItem: {
          "@type": "ListItem";
          position: number;
          name: string;
          item?: string | { "@id": string };
        } = {
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
        };

        // Last item uses @id reference, others use plain URL
        if (item.url) {
          if (index === breadcrumbs.length - 1) {
            breadcrumbItem.item = { "@id": item.url };
          } else {
            breadcrumbItem.item = item.url;
          }
        }

        return breadcrumbItem;
      }),
    };

    collectionPage.breadcrumb = breadcrumbList;
  }

  // Add copyright and publisher metadata
  if (options?.copyrightHolderId) {
    collectionPage.copyrightHolder = { "@id": options.copyrightHolderId };
  }

  if (options?.copyrightNotice) {
    collectionPage.copyrightNotice = options.copyrightNotice;
  }

  if (options?.copyrightYear) {
    collectionPage.copyrightYear = options.copyrightYear;
  }

  if (options?.publisherId) {
    collectionPage.publisher = { "@id": options.publisherId };
  }

  return collectionPage;
}

/**
 * Generates a Product schema object from ProductData
 * @param productData - Product information for schema generation
 * @returns Product schema object
 */
export function generateProduct(productData: ProductData): Product {
  const offers: Offer[] = [];

  // Add offers for each variation if they exist
  if (productData.variations && productData.variations.length > 0) {
    productData.variations.forEach((variation) => {
      offers.push(
        generateOffer(
          variation.price,
          productData.currency,
          variation.availability,
          productData.url,
          productData.regularPrice,
          productData.seller,
          productData.sellerUrl,
          variation.sku,
          variation.size,
          productData.sellerIdRef
        )
      );
    });
  } else {
    // If no variations, use main product data
    offers.push(
      generateOffer(
        productData.price,
        productData.currency,
        productData.availability,
        productData.url,
        productData.regularPrice,
        productData.seller,
        productData.sellerUrl,
        productData.sku,
        productData.size,
        productData.sellerIdRef
      )
    );
  }

  const product: Product = {
    "@type": "Product",
    name: productData.name,
    description: productData.description,
    image: productData.images,
    sku: productData.sku,
    offers: offers.length > 1 ? offers : offers[0],
    url: productData.url,
  };

  // Add brand information with optional sameAs links
  product.brand = generateBrand(
    productData.brand,
    productData.brandUrl,
    productData.brandSameAs
  );

  // Add optional fields if they exist
  if (productData.gtin) {
    product.gtin = productData.gtin;
  }

  if (productData.mpn) {
    product.mpn = productData.mpn;
  }

  if (productData.color) {
    product.color = productData.color;
  }

  if (productData.material) {
    product.material = productData.material;
  }

  if (productData.size) {
    product.size = productData.size;
  }

  if (productData.condition) {
    product.itemCondition = productData.condition;
  }

  if (productData.category) {
    product.category = productData.category;
  }

  if (productData.careInstructions) {
    addAdditionalProperty(
      product,
      "careInstructions",
      productData.careInstructions
    );
  }

  if (productData.buyActionUrl) {
    product.potentialAction = generateBuyAction(productData.buyActionUrl);
  }

  return product;
}

/**
 * Generates an OfferCatalog schema object for product listing pages
 * @param name - The catalog name
 * @param description - The catalog description
 * @param url - The catalog URL
 * @param products - Array of ProductData objects to include in the catalog
 * @param inLanguage - Optional language code for the catalog
 * @param breadcrumbs - Optional breadcrumb items to include in the catalog
 * @returns OfferCatalog schema object
 */
export function generateOfferCatalog(
  name: string,
  description: string,
  url: string,
  products: ProductData[],
  breadcrumbs?: Array<{ name: string; url?: string }>
): OfferCatalog {
  const offerCatalog: OfferCatalog = {
    "@type": "OfferCatalog",
    name,
    description,
    url,
  };

  // Convert products to Product schema objects using the reusable function
  const productSchemas: Product[] = products.map((productData) =>
    generateProduct(productData)
  );

  offerCatalog.numberOfItems = products.length;
  offerCatalog.itemListElement = productSchemas;

  // Add breadcrumbs if provided
  //   if (breadcrumbs && breadcrumbs.length > 0) {
  //     const breadcrumbList: BreadcrumbList = {
  //       "@type": "BreadcrumbList",
  //       itemListElement: breadcrumbs.map((item, index) => {
  //         const breadcrumbItem: {
  //           "@type": "ListItem";
  //           position: number;
  //           name: string;
  //           item?: string;
  //         } = {
  //           "@type": "ListItem",
  //           position: index + 1,
  //           name: item.name,
  //         };

  //         // Only add URL if it exists (last item typically doesn't have URL)
  //         if (item.url) {
  //           breadcrumbItem.item = item.url;
  //         }

  //         return breadcrumbItem;
  //       }),
  //     };

  //     offerCatalog.breadcrumb = breadcrumbList;
  //   }

  return offerCatalog;
}

/**
 * Generates an ItemList schema object for product listing pages.
 * Uses ListItem wrappers with position around each Product — matching
 * the structure marketing/SEO teams expect for PLPs.
 *
 * @param name - The list name (typically the h1 of the page)
 * @param url - The canonical URL of the listing page
 * @param products - Array of ProductData objects to include
 * @param description - Optional meta description
 * @returns Record representing the ItemList (using plain object for @id support)
 */
export function generateItemList(
  name: string,
  url: string,
  products: ProductData[],
  description?: string
): Record<string, unknown> {
  const itemListElement = products.map((productData, index) => {
    const product = generateProduct(productData);

    return {
      "@type": "ListItem",
      position: index + 1,
      item: product,
    };
  });

  const itemList: Record<string, unknown> = {
    "@id": `${url}#mainEntityOfPage`,
    "@type": "ItemList",
    name,
    itemListElement,
    mainEntityOfPage: {
      "@id": url,
    },
  };

  if (description) {
    itemList.description = description;
  }

  return itemList;
}
